import { AlertTriangle, RefreshCw, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface QueryErrorBoundaryProps {
  error: Error | null;
  refetch?: () => void;
  compact?: boolean;
  message?: string;
}

function isNetworkError(error: Error | null): boolean {
  if (!error) return false;
  const msg = error.message?.toLowerCase() || '';
  return msg.includes('fetch') || msg.includes('network') || msg.includes('failed to fetch') || msg.includes('load failed') || !navigator.onLine;
}

export function QueryErrorBoundary({ error, refetch, compact = false, message }: QueryErrorBoundaryProps) {
  const isNetwork = isNetworkError(error);
  const Icon = isNetwork ? WifiOff : AlertTriangle;
  const title = isNetwork ? 'Không có kết nối mạng' : 'Đã xảy ra lỗi';
  const description = message || (isNetwork
    ? 'Vui lòng kiểm tra kết nối mạng và thử lại.'
    : 'Không thể tải dữ liệu. Vui lòng thử lại sau.');

  if (compact) {
    return (
      <div className="flex items-center gap-3 p-4 rounded-xl bg-destructive/10 border border-destructive/20">
        <Icon className="w-5 h-5 text-destructive flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">{title}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
        {refetch && (
          <Button variant="outline" size="sm" onClick={refetch} className="flex-shrink-0">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
        <Icon className="w-8 h-8 text-destructive" />
      </div>
      <h2 className="text-lg font-bold text-foreground mb-2">{title}</h2>
      <p className="text-sm text-muted-foreground mb-6 max-w-xs">{description}</p>
      {refetch && (
        <Button onClick={refetch} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Thử lại
        </Button>
      )}
    </div>
  );
}
